import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { formatApiError } from "@/lib/api";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { GoogleButton } from "@/components/GoogleButton";
import { PhoneInput } from "@/components/PhoneInput";
import { BILLING_COUNTRIES, billingConfig, validateBilling } from "@/lib/billing";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function Register() {
  const { register } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "+39", company_name: "", role: "client", person_type: "individual", billing_country: "IT", vat_number: "", tax_code: "", sdi_code: "", pec_email: "", billing_address: "" });
  const [loading, setLoading] = useState(false);
  const isCompany = form.person_type === "company";
  const bcfg = billingConfig(form.billing_country);
  const accountType = form.role === "partner" ? "partner_company" : (isCompany ? "client_company" : "client_individual");
  const setAccountType = (v) => {
    if (v === "partner_company") setForm({ ...form, role: "partner", person_type: "company" });
    else if (v === "client_company") setForm({ ...form, role: "client", person_type: "company" });
    else setForm({ ...form, role: "client", person_type: "individual" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (isCompany) {
      const errs = validateBilling(form.billing_country, form);
      if (errs.length) { toast.error(errs[0]); return; }
    }
    setLoading(true);
    try {
      const payload = { ...form };
      if (isCompany) payload.company_name = form.name;
      await register(payload);
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <div className="flex justify-between items-center p-4">
        <Link to="/" className="font-display text-xl font-bold tracking-tight text-slate-900">Rimpatrio<span className="text-blue-600">24</span></Link>
        <LanguageSwitcher />
      </div>
      <div className="flex-1 flex items-center justify-center px-6 pb-16">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
          <h1 className="font-display text-2xl font-bold text-slate-900">{t("auth.register")}</h1>
          <div className="mt-5"><GoogleButton /></div>
          <div className="flex items-center gap-3 my-5">
            <div className="h-px bg-slate-200 flex-1" />
            <span className="text-xs uppercase tracking-wider text-slate-400">{t("auth.orDivider")}</span>
            <div className="h-px bg-slate-200 flex-1" />
          </div>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="space-y-1.5"><Label>Tipo di account</Label>
              <Select value={accountType} onValueChange={setAccountType}>
                <SelectTrigger data-testid="register-account-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="client_individual">Cliente — privato</SelectItem>
                  <SelectItem value="client_company">Cliente — azienda (con fattura)</SelectItem>
                  <SelectItem value="partner_company">Azienda partner — onoranze funebri</SelectItem>
                </SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>{isCompany ? t("auth.companyName") : t("auth.name")}</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="register-name" placeholder={isCompany ? "Es. Onoranze Funebri SRL" : ""} /></div>
            <div className="space-y-1.5"><Label>{t("auth.email")}</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="register-email" /></div>
            <div className="space-y-1.5"><Label>{t("auth.password")}</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="register-password" /></div>
            <div className="space-y-1.5"><Label>{t("auth.phone")}</Label>
              <PhoneInput value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="register-phone" /></div>
            {isCompany && (
              <p className="text-xs text-slate-400" data-testid="register-company-hint">{t("auth.companyHint")}</p>
            )}
            {isCompany && (
              <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="register-billing">
                <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Dati di fatturazione</p>
                <div className="space-y-1.5"><Label>Paese di fatturazione</Label>
                  <Select value={form.billing_country} onValueChange={(v) => setForm({ ...form, billing_country: v })}>
                    <SelectTrigger data-testid="register-billing-country"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {BILLING_COUNTRIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>{bcfg.vatLabel}</Label>
                    <Input value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} placeholder={bcfg.vatHint} data-testid="register-vat" />
                    {bcfg.vatHint && <p className="text-[11px] text-slate-400">{bcfg.vatHint}</p>}</div>
                  <div className="space-y-1.5"><Label>Codice Fiscale</Label>
                    <Input value={form.tax_code} onChange={(e) => setForm({ ...form, tax_code: e.target.value })} data-testid="register-taxcode" /></div>
                </div>
                {bcfg.italy && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label>Codice SDI</Label>
                      <Input value={form.sdi_code} onChange={(e) => setForm({ ...form, sdi_code: e.target.value })} placeholder="7 caratteri" maxLength={7} data-testid="register-sdi" /></div>
                    <div className="space-y-1.5"><Label>PEC</Label>
                      <Input type="email" value={form.pec_email} onChange={(e) => setForm({ ...form, pec_email: e.target.value })} data-testid="register-pec" /></div>
                  </div>
                )}
                <div className="space-y-1.5"><Label>Indirizzo di fatturazione</Label>
                  <Input value={form.billing_address} onChange={(e) => setForm({ ...form, billing_address: e.target.value })} data-testid="register-billing-address" /></div>
                <p className="text-xs text-slate-400">Questi dati verranno usati per emettere la fattura.</p>
              </div>
            )}
            <Button type="submit" disabled={loading} data-testid="register-submit" className="w-full bg-blue-900 hover:bg-blue-800 h-11">
              {loading ? "..." : t("auth.register")}
            </Button>
          </form>
          <p className="mt-6 text-sm text-slate-500">
            {t("nav.login")}? <Link to="/login" className="text-blue-900 font-medium hover:underline">{t("auth.login")}</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
